import { SERVER_FIELD_MAP } from '@src/features/users/constants';
import { branchSearchFromDto } from '@src/features/users/lib/branch.ts';
import { departmentFromDto } from '@src/features/users/lib/department.ts';
import { jobTitleFromDto } from '@src/features/users/lib/jobtitle';
import { rankFromDto } from '@src/features/users/lib/rank.ts';
import { teamFromDto } from '@src/features/users/lib/team';
import { titleFromDto } from '@src/features/users/lib/title';
import {
  buildUserWarningSections,
  USER_WARNING_PAYLOAD_FIELDS,
} from '@src/features/users/lib/userWarnings';
import type {
  ShortUser,
  ShortUserDto,
  UserDetail,
  UserDetailDto,
  UserList,
  UserListDto,
  UserSharedDetail,
  UserSharedDetailDto,
  UserUpdateDto,
  UserWarningEvaluatePayload,
} from '@src/features/users/types';
import type { AddUserFormData } from '@src/pages/Users/AddUser/types';
import {
  type AppLanguage,
  DEFAULT_APP_LANGUAGE,
  getIntlLocaleTag,
} from '@src/shared';

function dateToDtoString(date: Date | null): string | undefined {
  if (!date) {
    return undefined;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function hasOwn<T extends object, K extends PropertyKey>(
  value: T,
  key: K,
): value is T & Record<K, unknown> {
  return Object.hasOwn(value, key);
}

type UserUpdateForm = Omit<Partial<AddUserFormData>, 'country'> & {
  country?: AddUserFormData['country'] | string | number | null;
  signature?: File | null;
};

const USER_SERVER_FIELD_MAP: Record<string, string> = {
  user_first_name: 'firstName',
  user_family_name: 'familyName',
  user_initials: 'initials',
  user_birthdate: 'birthdate',
  user_titl: 'title',
  user_portrait: 'portrait',
  user_signature: 'signature',
  user_joti: 'jobTitle',
  user_team: 'team',
  user_bran_workplace: 'branch',
  user_dept: 'department',
  user_user_manager: 'manager',
  user_user_hr: 'hr',
  user_user_deputy: 'deputy',
  user_mobile: 'mobile',
  user_phone: 'phone',
  user_email_private: 'emailPrivate',
  user_mobile_private: 'mobilePrivate',
  user_phone_private: 'phonePrivate',
  user_ctry: 'country',
  user_city: 'city',
  user_postcode: 'postcode',
  user_address_line_1: 'addressLine1',
  user_address_line_2: 'addressLine2',
  user_emergency_contact1: 'emergency.0.contact',
  user_emergency_relationship1: 'emergency.0.relationship',
  user_emergency_mobile1: 'emergency.0.mobile',
  user_emergency_email1: 'emergency.0.email',
  user_emergency_contact2: 'emergency.1.contact',
  user_emergency_relationship2: 'emergency.1.relationship',
  user_emergency_mobile2: 'emergency.1.mobile',
  user_emergency_email2: 'emergency.1.email',
};

export function mapUserServerErrorField(field: string): string {
  return USER_SERVER_FIELD_MAP[field] ?? field;
}

export function shortUserFromDto(user: ShortUserDto): ShortUser {
  return {
    id: user.id,
    name: user.name,
    initials: user.user_initials,
    firstName: user.user_first_name,
    familyName: user.user_family_name,
    email: user.email,
    portrait: user.user_portrait,
  };
}

export function userListFromDto(user: UserListDto): UserList {
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    firstName: user.user_first_name,
    familyName: user.user_family_name,
    language: user.language,
    manager: user.user_user_manager
      ? shortUserFromDto(user.user_user_manager)
      : null,
    hr: user.user_user_hr,
    portrait: user.user_portrait,
    jobTitle: user.user_joti ? jobTitleFromDto(user.user_joti) : null,
    rank: user.user_rank ? rankFromDto(user.user_rank) : null,
    status: user.status,
    salaryPercentage: user.salary_percentage,
    branch: user.user_bran_workplace
      ? branchSearchFromDto(user.user_bran_workplace)
      : null,
    initials: user.user_initials,
    startDate: user.start_date,
    department: user.user_dept,
    team: user.user_team,
    deputy: user.user_user_deputy,
    title: user.user_titl,
    accessRoles: user.access_roles ?? [],
    warningSummary: user.warning_summary,
  };
}

export function userSharedDetailFromDto(
  user: UserSharedDetailDto,
): UserSharedDetail {
  const activeSalary = (user.user_salaries || []).find(
    (salary) => salary.is_active,
  );
  const salary = activeSalary
    ? {
        salaryStartDate: activeSalary.saly_start
          ? new Date(activeSalary.saly_start)
          : null,
        salaryEndDate: activeSalary.saly_end
          ? new Date(activeSalary.saly_end)
          : null,
        salaryPercentage: activeSalary.saly_percentage,
        salaryType: (activeSalary.saly_monthly_salary
          ? 'monthly'
          : 'hourly') as UserDetail['salaryType'],
        salaryMonthly: activeSalary.saly_monthly,
        salaryHourly: activeSalary.saly_hourly,
      }
    : {
        salaryStartDate: null,
        salaryEndDate: null,
        salaryPercentage: null,
        salaryType: 'monthly' as UserDetail['salaryType'],
        salaryMonthly: null,
        salaryHourly: null,
      };
  const emergency = [
    {
      contact: user.user_emergency_contact1,
      relationship: user.user_emergency_relationship1,
      mobile: user.user_emergency_mobile1,
      email: user.user_emergency_email1,
    },
  ];
  if (
    user.user_emergency_contact2 ||
    user.user_emergency_relationship2 ||
    user.user_emergency_mobile2 ||
    user.user_emergency_email2
  ) {
    emergency.push({
      contact: user.user_emergency_contact2,
      relationship: user.user_emergency_relationship2,
      mobile: user.user_emergency_mobile2,
      email: user.user_emergency_email2,
    });
  }
  return {
    id: user.id,
    email: user.email,
    name: user.name,
    firstName: user.user_first_name,
    familyName: user.user_family_name,
    initials: user.user_initials,
    language: user.language,
    status: user.status,
    branch: user.user_bran_workplace
      ? branchSearchFromDto(user.user_bran_workplace)
      : null,
    portrait: user.user_portrait,
    rank: user.user_rank ? rankFromDto(user.user_rank) : null,
    jobTitle: user.user_joti ? jobTitleFromDto(user.user_joti) : null,
    manager: user.user_user_manager
      ? shortUserFromDto(user.user_user_manager)
      : null,
    hr: user.user_user_hr ? shortUserFromDto(user.user_user_hr) : null,
    title: user.user_titl ? titleFromDto(user.user_titl) : null,
    team: user.user_team ? teamFromDto(user.user_team) : null,
    department: user.user_dept ? departmentFromDto(user.user_dept) : null,
    deputy: user.user_user_deputy
      ? shortUserFromDto(user.user_user_deputy)
      : null,
    accessRoles: user.access_roles ?? [],
    warningSummary: user.warning_summary,
    ...salary,
    signature: user.user_signature,
    birthdate: user.user_birthdate ? new Date(user.user_birthdate) : null,
    mobile: user.user_mobile,
    phone: user.user_phone ?? '',
    emailPrivate: user.user_email_private,
    mobilePrivate: user.user_mobile_private,
    phonePrivate: user.user_phone_private,
    addressLine1: user.user_address_line_1,
    addressLine2: user.user_address_line_2,
    city: user.user_city,
    postcode: user.user_postcode,
    country: user.user_ctry,
    emergency,
    permissions: user.permissions ?? [],
  };
}

export function userDetailFromDto(user: UserDetailDto): UserDetail {
  return {
    ...userSharedDetailFromDto(user),
    rankName: user.rank_name,
    companyName: user.company_name,
    warningSections: buildUserWarningSections(user.warning_summary.warnings),
    profileCapabilities: {
      readableSections: new Set(user.profile_capabilities.readable_sections),
      editableFields: new Set(user.profile_capabilities.editable_fields),
      actions: {
        changePassword: user.profile_capabilities.actions.change_password,
        resetPassword: user.profile_capabilities.actions.reset_password,
        resetTwoFactor: user.profile_capabilities.actions.reset_two_factor,
        editSchedule: user.profile_capabilities.actions.edit_schedule,
        approveSchedule: user.profile_capabilities.actions.approve_schedule,
        editCareer: user.profile_capabilities.actions.edit_career,
        editVacationCredit:
          user.profile_capabilities.actions.edit_vacation_credit,
      },
    },
  };
}

export function userUpdateToDto(user: AddUserFormData): UserUpdateDto;
export function userUpdateToDto(user: UserUpdateForm): Partial<UserUpdateDto>;
export function userUpdateToDto(user: UserUpdateForm) {
  const payload: Partial<UserUpdateDto> = {};

  if (hasOwn(user, 'email')) {
    payload.email = user.email as string;
  }
  if (hasOwn(user, 'firstName')) {
    payload.user_first_name = user.firstName as string;
  }
  if (hasOwn(user, 'familyName')) {
    payload.user_family_name = user.familyName as string;
  }
  if (hasOwn(user, 'initials')) {
    payload.user_initials = user.initials as string;
  }
  if (hasOwn(user, 'language')) {
    payload.language = getIntlLocaleTag(
      ((user.language as string) || DEFAULT_APP_LANGUAGE) as AppLanguage,
    );
  }
  if (hasOwn(user, 'branch')) {
    payload.user_bran_workplace = user.branch?.id || null;
  }
  if (hasOwn(user, 'portrait')) {
    payload.user_portrait = user.portrait || null;
  }
  if (hasOwn(user, 'jobTitleId')) {
    payload.user_joti = user.jobTitleId || null;
  }
  if (hasOwn(user, 'manager')) {
    payload.user_user_manager = user.manager?.id || null;
  }
  if (hasOwn(user, 'hr')) {
    payload.user_user_hr = user.hr?.id || null;
  }
  if (hasOwn(user, 'title')) {
    payload.user_titl = user.title || null;
  }
  if (hasOwn(user, 'team')) {
    payload.user_team = user.team?.id || null;
  }
  if (hasOwn(user, 'department')) {
    payload.user_dept = user.department?.id || null;
  }
  if (hasOwn(user, 'deputy')) {
    payload.user_user_deputy = user.deputy?.id || null;
  }
  if (hasOwn(user, 'signature')) {
    payload.user_signature = user.signature ?? null;
  }
  if (hasOwn(user, 'birthdate')) {
    payload.user_birthdate = dateToDtoString(user.birthdate ?? null);
  }
  if (hasOwn(user, 'mobile')) {
    payload.user_mobile = user.mobile || null;
  }
  if (hasOwn(user, 'phone')) {
    payload.user_phone = user.phone as string;
  }
  if (hasOwn(user, 'emailPrivate')) {
    payload.user_email_private = user.emailPrivate || null;
  }
  if (hasOwn(user, 'mobilePrivate')) {
    payload.user_mobile_private = user.mobilePrivate || null;
  }
  if (hasOwn(user, 'phonePrivate')) {
    payload.user_phone_private = user.phonePrivate || null;
  }
  if (hasOwn(user, 'addressLine1')) {
    payload.user_address_line_1 = user.addressLine1 || null;
  }
  if (hasOwn(user, 'addressLine2')) {
    payload.user_address_line_2 = user.addressLine2 || null;
  }
  if (hasOwn(user, 'city')) {
    payload.user_city = user.city || null;
  }
  if (hasOwn(user, 'postcode')) {
    payload.user_postcode = user.postcode || null;
  }
  if (hasOwn(user, 'country')) {
    const country = user.country;
    payload.user_ctry =
      typeof country === 'object' ? (country?.id ?? null) : country || null;
  }
  if (hasOwn(user, 'emergency')) {
    payload.user_emergency_contact1 = user.emergency?.[0]?.contact || null;
    payload.user_emergency_relationship1 =
      user.emergency?.[0]?.relationship || null;
    payload.user_emergency_mobile1 = user.emergency?.[0]?.mobile || null;
    payload.user_emergency_email1 = user.emergency?.[0]?.email || null;
    payload.user_emergency_contact2 = user.emergency?.[1]?.contact || null;
    payload.user_emergency_relationship2 =
      user.emergency?.[1]?.relationship || null;
    payload.user_emergency_mobile2 = user.emergency?.[1]?.mobile || null;
    payload.user_emergency_email2 = user.emergency?.[1]?.email || null;
  }
  if (hasOwn(user, 'salaryId')) {
    payload.salary_id = user.salaryId;
  }
  if (hasOwn(user, 'salaryStartDate')) {
    payload.salary_saly_start = dateToDtoString(
      user.salaryStartDate ?? null,
    ) as string;
  }
  if (hasOwn(user, 'salaryEndDate')) {
    payload.salary_saly_end =
      dateToDtoString(user.salaryEndDate ?? null) ?? null;
  }
  if (hasOwn(user, 'salaryPercentage')) {
    payload.salary_saly_percentage = user.salaryPercentage as number;
  }
  if (hasOwn(user, 'rankId')) {
    payload.salary_saly_rank = user.rankId || null;
  }
  if (hasOwn(user, 'salaryType')) {
    const isMonthly = user.salaryType !== 'hourly';
    payload.salary_saly_monthly_salary = isMonthly;
    payload.salary_saly_monthly = isMonthly
      ? (user.salaryMonthly as number)
      : null;
    payload.salary_saly_hourly = isMonthly
      ? null
      : (user.salaryHourly as number);
  } else {
    if (hasOwn(user, 'salaryMonthly')) {
      payload.salary_saly_monthly = user.salaryMonthly as number | null;
    }
    if (hasOwn(user, 'salaryHourly')) {
      payload.salary_saly_hourly = user.salaryHourly as number | null;
    }
  }

  return payload;
}

export function userWarningDraftToDto(
  user: AddUserFormData,
): UserWarningEvaluatePayload {
  const payload = userUpdateToDto(user);

  return {
    id: user.id,
    email: payload.email,
    user_initials: payload.user_initials,
    user_birthdate: payload.user_birthdate ?? null,
    user_email_private: payload.user_email_private ?? null,
    user_mobile_private: payload.user_mobile_private ?? null,
    user_user_manager:
      typeof payload.user_user_manager === 'number'
        ? payload.user_user_manager
        : null,
    user_user_hr:
      typeof payload.user_user_hr === 'number' ? payload.user_user_hr : null,
    user_user_deputy:
      typeof payload.user_user_deputy === 'number'
        ? payload.user_user_deputy
        : null,
    user_emergency_contact1: payload.user_emergency_contact1 ?? null,
    user_emergency_mobile1: payload.user_emergency_mobile1 ?? null,
    user_emergency_email1: payload.user_emergency_email1 ?? null,
    user_address_line_1: payload.user_address_line_1 ?? null,
    user_city: payload.user_city ?? null,
    user_postcode: payload.user_postcode ?? null,
    portrait_present: Boolean(user.portrait || user.portraitPreview),
  };
}

export function userDetailToWarningDto(
  user: UserDetail,
): UserWarningEvaluatePayload {
  return {
    id: user.id,
    email: user.email,
    user_initials: user.initials,
    user_birthdate: dateToDtoString(user.birthdate) ?? null,
    user_email_private: user.emailPrivate,
    user_mobile_private: user.mobilePrivate,
    user_user_manager: user.manager?.id ?? null,
    user_user_hr: user.hr?.id ?? null,
    user_user_deputy: user.deputy?.id ?? null,
    user_emergency_contact1: user.emergency[0]?.contact ?? null,
    user_emergency_mobile1: user.emergency[0]?.mobile ?? null,
    user_emergency_email1: user.emergency[0]?.email ?? null,
    user_address_line_1: user.addressLine1,
    user_city: user.city,
    user_postcode: user.postcode,
    portrait_present: Boolean(user.portrait),
    signature_present: Boolean(user.signature),
  };
}

const WARNING_TRIGGER_FIELD_BY_FORM_FIELD: Record<string, string> = {
  ...Object.fromEntries(
    Object.entries(SERVER_FIELD_MAP).map(([serverField, formField]) => [
      formField,
      serverField,
    ]),
  ),
  email: 'email',
  portrait: 'portrait_present',
  signature: 'signature_present',
};

export function getUserWarningTriggerField(formField: string) {
  return WARNING_TRIGGER_FIELD_BY_FORM_FIELD[formField] ?? formField;
}

export function mergeUserWarningSectionPayloads(
  base: UserWarningEvaluatePayload,
  sections: Record<string, Record<string, unknown>>,
): UserWarningEvaluatePayload {
  const updates = Object.assign({}, ...Object.values(sections)) as Record<
    string,
    unknown
  >;
  const payload = { ...base };

  USER_WARNING_PAYLOAD_FIELDS.forEach((field) => {
    if (Object.hasOwn(updates, field)) {
      Object.assign(payload, { [field]: updates[field] });
    }
  });
  if (Object.hasOwn(updates, 'user_portrait')) {
    payload.portrait_present = Boolean(updates.user_portrait);
  }
  if (Object.hasOwn(updates, 'user_signature')) {
    payload.signature_present = Boolean(updates.user_signature);
  }

  return payload;
}
